<template>
	<v-dialog v-model="localDialogOpen" width="480px" persistent>
		<v-card color="rgba(41, 41, 47, 0.8)">
			<v-card-title class="text-h6">{{ getTitleKey() }}</v-card-title>
			<v-card-text>
				<v-row class="ma-0">
					<v-col cols="12">
						<!-- Name -->
						<SolidMeshPropertyFormTable transparent>
							<tr>
								<td>{{ $t("NAME") }}</td>
								<td>
									<v-text-field v-model="formData.name" variant="underlined" density="compact"
										:rules="[v => !!(v && v.trim() !== '')]" hide-details />
								</td>
							</tr>
						</SolidMeshPropertyFormTable>
						<!-- Size -->
						<SolidMeshPropertyFormTable transparent>
							<!-- Box Culvert Parameters -->
							<template v-if="selectedStructureType === getSimpleStructureType.BOX_CULVERT">
								<tr>
									<td>{{ $t("CULVERT_WIDTH") }}</td>
									<td>
										<DistanceSettingInput v-model="formData.boxCulvert.width" base-unit="mm" variant="underlined"
											density="compact" :step="SIMPLE_STRUCTURE_DIMENSION_MIN"
											:min-value="SIMPLE_STRUCTURE_DIMENSION_MIN"
											:max-value="SIMPLE_STRUCTURE_COMMON_CONSTRAINT_MAX_VALUE" :decimal-length="DIMENSION_DECIMALS"
											hide-details />
									</td>
								</tr>
								<tr>
									<td>{{ $t("CULVERT_HEIGHT") }}</td>
									<td>
										<DistanceSettingInput v-model="formData.boxCulvert.height" base-unit="mm" variant="underlined"
											density="compact" :step="SIMPLE_STRUCTURE_DIMENSION_MIN"
											:min-value="SIMPLE_STRUCTURE_DIMENSION_MIN"
											:max-value="SIMPLE_STRUCTURE_COMMON_CONSTRAINT_MAX_VALUE" :decimal-length="DIMENSION_DECIMALS"
											hide-details />
									</td>
								</tr>
								<tr>
									<td>{{ $t("CULVERT_LENGTH") }}</td>
									<td>
										<DistanceSettingInput v-model="formData.boxCulvert.length" variant="underlined" density="compact"
											:step="SIMPLE_STRUCTURE_DIMENSION_MIN" :min-value="SIMPLE_STRUCTURE_DIMENSION_MIN"
											:max-value="SIMPLE_STRUCTURE_DIMENSION_MAX" :decimal-length="DIMENSION_DECIMALS" hide-details />
									</td>
								</tr>
							</template>
							<!-- Manhole Parameters -->
							<template v-if="selectedStructureType === getSimpleStructureType.MANHOLE">
								<tr @mouseenter="hoveredManholeDiameter = true" @mouseleave="hoveredManholeDiameter = false">
									<td rowspan="2" :class="{ 'row-title': true, 'highlight-title': hoveredManholeDiameter }">{{
										$t("MANHOLE_DIAMETER") }}</td>
									<td>
										<v-select v-model="formData.manhole.standardSize" :items="manholeStandardSizesWithCustom"
											item-title="label" item-value="value" variant="underlined" density="compact" hide-details
											class="ma-0" @update:model-value="onManholeStandardSizeChange" />
									</td>
								</tr>
								<tr
									:class="{ 'manhole-custom-row': true, 'manhole-custom-row--hidden': formData.manhole.standardSize !== 'custom' }"
									@mouseenter="hoveredManholeDiameter = true" @mouseleave="hoveredManholeDiameter = false">
									<td>
										<DistanceSettingInput v-model="formData.manhole.diameter" base-unit="mm" variant="underlined"
											density="compact" :step="SIMPLE_STRUCTURE_DIMENSION_MIN"
											:min-value="SIMPLE_STRUCTURE_DIMENSION_MIN"
											:max-value="SIMPLE_STRUCTURE_COMMON_CONSTRAINT_MAX_VALUE" :decimal-length="DIMENSION_DECIMALS"
											hide-details :disabled="formData.manhole.standardSize !== 'custom'" />
									</td>
								</tr>
								<tr>
									<td>{{ $t("MANHOLE_DEPTH") }}</td>
									<td>
										<DistanceSettingInput v-model="formData.manhole.depth" variant="underlined" density="compact"
											:step="SIMPLE_STRUCTURE_DIMENSION_MIN" :min-value="SIMPLE_STRUCTURE_DIMENSION_MIN"
											:max-value="SIMPLE_STRUCTURE_DIMENSION_MAX" :decimal-length="DIMENSION_DECIMALS" hide-details />
									</td>
								</tr>
							</template>
						</SolidMeshPropertyFormTable>
						<!-- Control point (single) -->
						<SolidMeshPropertyFormTable transparent>
							<tr @mouseenter="hoveredSingle = true" @mouseleave="hoveredSingle = false">
								<td rowspan="3" :class="{ 'row-title': true, 'highlight-title': hoveredSingle }">{{ $t("BASIS_POINT") }}
								</td>
								<td class="coordinate-label">{{ $t("X") }}</td>
								<td>
									<DistanceSettingInput v-model="formData.position.x" variant="underlined" density="compact"
										:step="anchorPointStep" :decimal-length="anchorPointDecimals" hide-details />
								</td>
							</tr>
							<tr @mouseenter="hoveredSingle = true" @mouseleave="hoveredSingle = false">
								<td class="coordinate-label">{{ $t("Y") }}</td>
								<td>
									<DistanceSettingInput v-model="formData.position.y" variant="underlined" density="compact"
										:step="anchorPointStep" :decimal-length="anchorPointDecimals" hide-details />
								</td>
							</tr>
							<tr @mouseenter="hoveredSingle = true" @mouseleave="hoveredSingle = false">
								<td class="coordinate-label">{{ $t("Z") }}</td>
								<td>
									<DistanceSettingInput v-model="formData.position.z" variant="underlined" density="compact"
										:step="anchorPointStep" :decimal-length="anchorPointDecimals" hide-details />
								</td>
							</tr>
						</SolidMeshPropertyFormTable>
						<!-- Color -->
						<SolidMeshPropertyFormTable transparent>
							<tr>
								<td>{{ $t("COLOR") }}</td>
								<td>
									<v-menu :close-on-content-click="false" location="bottom start">
										<template v-slot:activator="{ props }">
											<v-avatar :color="rgbToHex(formData.color)" size="20px" v-bind="props" />
										</template>
										<v-card min-width="300">
											<v-color-picker v-model="pickerColor" mode="rgb" hide-inputs show-swatches
												@update:model-value="onPickerColorChange" />
										</v-card>
									</v-menu>
								</td>
							</tr>
						</SolidMeshPropertyFormTable>
					</v-col>
				</v-row>
			</v-card-text>
			<v-card-actions class="justify-end pa-4">
				<v-btn variant="flat" @click="cancel" color="red" size="small">
					{{ $t("CANCEL") }}
				</v-btn>
				<v-btn variant="flat" @click="confirm" :color="isFormValid ? 'primary' : 'grey'" :disabled="!isFormValid"
					size="small">
					{{ $t("ADD") }}
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
import { SIMPLE_STRUCTURE_TYPE } from "@/models/objects/solidMesh/types";
import {
	GoogleAnalyticsClickTypes,
	addClickTypeLog,
} from "@/utils/googleAnalytics.js";
import {
	buildBoxCulvertFromAnchor,
	buildManholeFromAnchor,
} from "earthbrain-viewer";
import DistanceSettingInput from "../DistanceSettingInput.vue";
import { SIMPLE_STRUCTURE_TYPES } from "./SimpleStructureTypeConfig";
import SolidMeshPropertyFormTable from "./SolidMeshPropertyFormTable.vue";
import { DIMENSION_DECIMALS } from "./composables/useSolidMeshDimensionConfig";
import { useSolidMeshUnitDisplay } from "./composables/useSolidMeshUnitDisplay";
import {
	getMaxDecimals,
	SIMPLE_STRUCTURE_EDIT_CONSTRAINTS,
	SIMPLE_STRUCTURE_DIMENSION_MAX,
	SIMPLE_STRUCTURE_COMMON_CONSTRAINT_MAX_VALUE,
	SIMPLE_STRUCTURE_DIMENSION_MIN,
} from "./composables/useSolidMeshEdit";

function createInitialFormData() {
	return {
		name: "",
		color: { r: 1, g: 1, b: 1, a: 1 },
		boxCulvert: {
			width: "0.00", // mm
			height: "0.00", // mm
			length: "0.00", // m
			gradient: { mode: "percent", value: 0 },
		},
		manhole: {
			standardSize: 600,
			diameter: "600.00", // mm
			depth: "0.00", // m
		},
		position: { x: 0, y: 0, z: 0 },
	};
}

export default {
	name: "PropertyDialogSimpleStructureSinglePoint",
	components: { SolidMeshPropertyFormTable, DistanceSettingInput },
	setup() {
		// DistanceSettingInput owns the unit conversion; only the decimal count
		// has to be resolved here so the dialog matches the property panels.
		const { isMetricDistance, convertLength } = useSolidMeshUnitDisplay();
		return { isMetricDistance, convertLength };
	},
	props: {
		modelValue: {
			type: Boolean,
			default: false,
		},
		selectedStructureType: {
			type: String,
			default: null,
		},
		initialPosition: {
			type: Object,
			default: null,
		},
		tempMeshId: {
			type: String,
			default: null,
		},
	},
	emits: ["confirm", "cancel", "update:modelValue"],
	data: () => ({
		formData: createInitialFormData(),
		localDialogOpen: false,
		suppressSync: false,
		hoveredSingle: false,
		hoveredManholeDiameter: false,
		previewUpdateTimer: null,
	}),
	computed: {
		DIMENSION_DECIMALS() {
			return DIMENSION_DECIMALS;
		},
		anchorPointDecimals() {
			return SIMPLE_STRUCTURE_EDIT_CONSTRAINTS.anchorPoint.decimalLength;
		},
		/**
		 * Standard manhole diameters offered for the site distance unit.
		 * Metric sites keep the existing mm range; feet sites use whole feet.
		 * Values stay in mm because that is the builder boundary unit.
		 */
		manholeStandardSizes() {
			if (this.isMetricDistance) {
				return [600, 750, 900, 1200, 1500, 1800, 2200].map((mm) => ({
					label: `${mm}mm`,
					value: mm,
				}));
			}
			return [1, 2, 3, 4, 5, 6, 7].map((ft) => ({
				label: `${ft}ft`,
				value: this.convertLength(ft, "site", "mm"),
			}));
		},
		SIMPLE_STRUCTURE_DIMENSION_MIN() {
			return SIMPLE_STRUCTURE_DIMENSION_MIN;
		},
		SIMPLE_STRUCTURE_DIMENSION_MAX() {
			return SIMPLE_STRUCTURE_DIMENSION_MAX;
		},
		SIMPLE_STRUCTURE_COMMON_CONSTRAINT_MAX_VALUE() {
			return SIMPLE_STRUCTURE_COMMON_CONSTRAINT_MAX_VALUE;
		},
		getSimpleStructureType() {
			return SIMPLE_STRUCTURE_TYPE;
		},
		anchorPointStep() {
			return SIMPLE_STRUCTURE_EDIT_CONSTRAINTS.anchorPoint.step;
		},
		isFormValid() {
			const dimensionMax = SIMPLE_STRUCTURE_DIMENSION_MAX;
			const dimensionMax_mm = SIMPLE_STRUCTURE_DIMENSION_MAX;
			if (this.formData.name.trim() === "") return false;
			if (
				!this.isNonEmptyFiniteNumber(this.formData.position.x) ||
				!this.isNonEmptyFiniteNumber(this.formData.position.y) ||
				!this.isNonEmptyFiniteNumber(this.formData.position.z)
			)
				return false;
			switch (this.selectedStructureType) {
				case SIMPLE_STRUCTURE_TYPE.BOX_CULVERT:
					return (
						this.formData.boxCulvert.width > 0 &&
						this.formData.boxCulvert.height > 0 &&
						this.formData.boxCulvert.length > 0 &&
						this.formData.boxCulvert.length <= dimensionMax_mm
					);
				case SIMPLE_STRUCTURE_TYPE.MANHOLE: {
					const hasValidSize =
						this.formData.manhole.standardSize === "custom"
							? this.formData.manhole.diameter > 0
							: this.formData.manhole.standardSize !== null;
					return (
						hasValidSize &&
						this.formData.manhole.depth > 0 &&
						this.formData.manhole.depth <= dimensionMax
					);
				}
				default:
					return false;
			}
		},
		pickerColor: {
			get() {
				return {
					r: Math.round((this.formData.color.r ?? 1) * 255),
					g: Math.round((this.formData.color.g ?? 1) * 255),
					b: Math.round((this.formData.color.b ?? 1) * 255),
				};
			},
			set(_) {},
		},
		manholeStandardSizesWithCustom() {
			return [
				...this.manholeStandardSizes,
				{ label: this.$t("MANHOLE_SIZE_CUSTOM"), value: "custom" },
			];
		},
	},
	watch: {
		localDialogOpen(newVal) {
			if (newVal === false) {
				this.$emit("update:modelValue", false);
			}
		},
		"formData.boxCulvert": {
			handler() {
				if (this.suppressSync) return;
				if (
					this.selectedStructureType === SIMPLE_STRUCTURE_TYPE.BOX_CULVERT &&
					this.tempMeshId
				) {
					this.schedulePreviewUpdate();
				}
			},
			deep: true,
		},
		"formData.manhole": {
			handler() {
				if (this.suppressSync) return;
				if (
					this.selectedStructureType === SIMPLE_STRUCTURE_TYPE.MANHOLE &&
					this.tempMeshId
				) {
					this.schedulePreviewUpdate();
				}
			},
			deep: true,
		},
		"formData.position": {
			handler() {
				if (this.suppressSync) return;
				if (this.tempMeshId) {
					this.schedulePreviewUpdate();
				}
			},
			deep: true,
		},
		"formData.color": {
			handler() {
				if (this.suppressSync) return;
				if (this.tempMeshId) {
					this.schedulePreviewUpdate();
				}
			},
			deep: true,
		},
		tempMeshId: {
			async handler(newMeshId) {
				if (!newMeshId) return;
				if (this.selectedStructureType === SIMPLE_STRUCTURE_TYPE.BOX_CULVERT) {
					this.suppressSync = true;
					try {
						const meshData = await this.$store.dispatch(
							"solidMesh/getSolidMeshJSON",
							{ meshId: newMeshId },
						);
						const dims = meshData.shapeConfig.dimensions;
						this.formData.boxCulvert = {
							// Kept unrounded; the input owns the displayed digits.
							width: dims.x * 1000,
							height: dims.z * 1000,
							length: Number(dims.y),
							gradient: { mode: "percent", value: 0 },
						};
					} catch (error) {
						console.error("Failed to get initial mesh dimensions:", error);
					}
					this.suppressSync = false;
				} else if (
					this.selectedStructureType === SIMPLE_STRUCTURE_TYPE.MANHOLE
				) {
					this.suppressSync = true;
					try {
						this.formData.manhole = this.initialManholeFormData();
					} catch (error) {
						console.error("Failed to set initial manhole values:", error);
					}
					this.suppressSync = false;
					await this.updateTempMeshPreview();
				}
			},
			immediate: true,
		},
		initialPosition: {
			handler(newVal) {
				if (newVal && typeof newVal === "object") {
					// Kept unrounded; the input owns the displayed digits.
					this.formData.position = {
						x: newVal.x ?? 0,
						y: newVal.y ?? 0,
						z: newVal.z ?? 0,
					};
				}
			},
			immediate: true,
			deep: true,
		},
	},
	mounted() {
		this.localDialogOpen = true;
		if (!this.initialPosition && !this.tempMeshId) {
			this.resetFormData();
		}
	},
	methods: {
		isNonEmptyFiniteNumber(v) {
			return (
				v !== "" && v !== null && v !== undefined && Number.isFinite(Number(v))
			);
		},
		/**
		 * Manhole defaults for a newly created structure.
		 * Metric sites keep the existing values; feet sites use 1 ft / 3 ft.
		 */
		initialManholeFormData() {
			if (this.isMetricDistance) {
				return { diameter: "600.00", depth: "1.00", standardSize: 600 };
			}
			const diameterMm = this.convertLength(1, "site", "mm");
			return {
				diameter: diameterMm,
				depth: this.convertLength(3, "site", "m"),
				standardSize: diameterMm,
			};
		},
		hasAllowedDecimals(value, maxValue) {
			if (value === null || value === undefined || value === "") return false;
			const parsed = Number(value);
			if (!Number.isFinite(parsed)) return false;
			const maxDecimals = getMaxDecimals(maxValue);
			const factor = 10 ** maxDecimals;
			const rounded = Math.round(parsed * factor) / factor;
			return Math.abs(parsed - rounded) < 1e-9;
		},
		resetFormData() {
			this.suppressSync = true;
			this.formData = createInitialFormData();
			this.suppressSync = false;
		},
		getTitleKey() {
			const keyMap = {
				[SIMPLE_STRUCTURE_TYPE.BOX_CULVERT]: "ADD_BOX_CULVERT",
				[SIMPLE_STRUCTURE_TYPE.MANHOLE]: "ADD_MANHOLE",
			};
			const key = keyMap[this.selectedStructureType];
			return key ? this.$t(key) : "";
		},
		roundTo3(v) {
			const n = Number(v);
			return Number.isFinite(n) ? Math.round(n * 1000) / 1000 : 0;
		},
		roundPointTo3(p) {
			const x = p && p.x !== undefined ? this.roundTo3(p.x) : 0;
			const y = p && p.y !== undefined ? this.roundTo3(p.y) : 0;
			const z = p && p.z !== undefined ? this.roundTo3(p.z) : 0;
			return { x, y, z };
		},
		rgbToHex(rgb) {
			if (!rgb) return "#ffffff";
			const r = Math.round((rgb.r ?? 0) * 255);
			const g = Math.round((rgb.g ?? 0) * 255);
			const b = Math.round((rgb.b ?? 0) * 255);
			return `#${[r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
		},
		onPickerColorChange(val) {
			if (typeof val === "object" && val !== null) {
				this.formData.color = {
					r: (val.r ?? 0) / 255,
					g: (val.g ?? 0) / 255,
					b: (val.b ?? 0) / 255,
					a: 1,
				};
			}
		},
		onManholeStandardSizeChange(sizeValue) {
			if (sizeValue === "custom") return;
			if (sizeValue !== null) {
				this.formData.manhole.diameter = Number(sizeValue).toFixed(2);
			}
		},
		buildViewerParams() {
			const P = this.formData.position;
			const pos = {
				x: Number(P.x) || 0,
				y: Number(P.y) || 0,
				z: Number(P.z) || 0,
			};
			switch (this.selectedStructureType) {
				case SIMPLE_STRUCTURE_TYPE.BOX_CULVERT:
					return buildBoxCulvertFromAnchor({
						P: pos,
						width_mm: Number(this.formData.boxCulvert.width) || 0,
						height_mm: Number(this.formData.boxCulvert.height) || 0,
						length_m: Number(this.formData.boxCulvert.length) || 0,
					});
				case SIMPLE_STRUCTURE_TYPE.MANHOLE:
					return buildManholeFromAnchor({
						P: pos,
						diameter_mm: Number(this.formData.manhole.diameter) || 0,
						depth_m: Number(this.formData.manhole.depth) || 0,
					});
				default:
					return undefined;
			}
		},
		schedulePreviewUpdate() {
			if (this.previewUpdateTimer) {
				clearTimeout(this.previewUpdateTimer);
			}
			this.previewUpdateTimer = setTimeout(() => {
				this.updateTempMeshPreview();
			}, 300);
		},
		async updateTempMeshPreview() {
			const type = this.selectedStructureType;
			if (
				!this.tempMeshId ||
				(type !== SIMPLE_STRUCTURE_TYPE.BOX_CULVERT &&
					type !== SIMPLE_STRUCTURE_TYPE.MANHOLE)
			)
				return;
			try {
				const computed = this.buildViewerParams();
				if (!computed) return;
				const d = computed.dimensions;
				if (!d.x || !d.y || !d.z) return;
				await this.$store.dispatch("solidMesh/applyPreviewParams", {
					meshId: this.tempMeshId,
					params: {
						dimensions: computed.dimensions,
						localCenter: computed.center,
						hprRad: {
							heading: computed.rotation?.z ?? 0,
							pitch: computed.rotation?.y ?? 0,
							roll: computed.rotation?.x ?? 0,
						},
					},
				});
				await this.$store.dispatch("solidMesh/setSolidMeshColor", {
					meshId: this.tempMeshId,
					colorArr: [
						this.formData.color?.r ?? 1,
						this.formData.color?.g ?? 1,
						this.formData.color?.b ?? 1,
					],
				});
				await this.$store.dispatch("solidMesh/setSolidMeshVisibility", {
					meshId: this.tempMeshId,
					visible: true,
				});
			} catch (error) {
				console.error("Failed to update mesh preview:", error);
			}
		},
		confirm() {
			const params = Object.assign(
				{},
				this.formData[
					this.selectedStructureType === SIMPLE_STRUCTURE_TYPE.BOX_CULVERT
						? "boxCulvert"
						: SIMPLE_STRUCTURE_TYPE.MANHOLE
				],
			);
			if (this.selectedStructureType === SIMPLE_STRUCTURE_TYPE.BOX_CULVERT) {
				params.width = Number(params.width) || 0;
				params.height = Number(params.height) || 0;
				params.length = Number(params.length) || 0;
			}
			if (this.selectedStructureType === SIMPLE_STRUCTURE_TYPE.MANHOLE) {
				params.diameter = Number(params.diameter) || 0;
				params.depth = Number(params.depth) || 0;
			}
			params.points = { p1: this.formData.position };

			const payload = {
				structureType: this.selectedStructureType,
				parameters: params,
				name: this.formData.name,
				color: {
					red: this.formData.color?.r ?? 1,
					green: this.formData.color?.g ?? 1,
					blue: this.formData.color?.b ?? 1,
					alpha: this.formData.color?.a ?? 1,
				},
			};
			const computed = this.buildViewerParams();
			if (computed) payload.viewerParams = computed;

			this.$emit("confirm", payload);
			const confirmMap = {
				[SIMPLE_STRUCTURE_TYPE.BOX_CULVERT]:
					GoogleAnalyticsClickTypes.P_CLICK_ADD_SIMPLE_STRUCTURE_BOX_CULVERT,
				[SIMPLE_STRUCTURE_TYPE.MANHOLE]:
					GoogleAnalyticsClickTypes.P_CLICK_ADD_SIMPLE_STRUCTURE_MANHOLE,
			};
			const ct = confirmMap[this.selectedStructureType];
			if (ct) addClickTypeLog(ct);
			this.localDialogOpen = false;
			this.$emit("update:modelValue", false);
		},
		async cancel() {
			if (this.tempMeshId) {
				try {
					await this.$store.dispatch("solidMesh/deleteSolidMesh", {
						meshId: this.tempMeshId,
					});
				} catch (error) {
					console.error("Failed to delete temp mesh on cancel:", error);
				}
			}
			this.$emit("cancel");
			this.localDialogOpen = false;
			this.$emit("update:modelValue", false);
		},
	},
	beforeUnmount() {
		if (this.previewUpdateTimer) {
			clearTimeout(this.previewUpdateTimer);
			this.previewUpdateTimer = null;
		}
	},
};
</script>

<style scoped>
.section-title {
	font-weight: 600;
	font-size: 0.95rem;
	color: rgba(255, 255, 255, 0.87);
	border-bottom: 1px solid rgba(255, 255, 255, 0.12);
	padding-bottom: 8px;
}
</style>
